import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import { GygListDataResponse, Meta } from '@/src/types/apps/commonTypes'
import { MasterProduct } from '@/src/types/apps/gygMenuContainerTypes'
import { GygQueryOptions, PricingTier } from '@/src/views/components/master-pricing-list/types'
import { getSanitisedParams } from '../gygMenuContainer'
import { stringify } from 'qs'

interface MasterProductInitialState {
  masterProducts: MasterProduct[] | []
  menuContainers: MenuContainersWithMasterProduct[] | []
  meta: Meta | null
  loading: boolean
  success: string | null
  error: string | null
  selectedProductId: number | null
  isDialogOpen: boolean

  saveSuccess: boolean | null
  saveError: string | null
  masterProductRelationships: any[]
}

export interface MasterProductTierPrice {
  pricingTier: PricingTier
  value: number
  effectiveFrom: string
}

export interface MasterProductCreatePayload {
  menuContainerProductIds: number[]
  sectionIds: number[]
  name: string
}
export interface MasterProductRelationshipSection {
  sections: number[]
}

export interface MasterProductPriceUpdatePayload {
  id: number
  payload: MasterProductTierPrice[]
}

interface MenuContainersWithMasterProduct {
  id: number
  name: string
  masterProducts: MasterProduct[]
}

const initialState: MasterProductInitialState = {
  masterProducts: [],
  menuContainers: [],
  meta: null,
  loading: false,
  success: null,
  error: null,
  isDialogOpen: false,
  selectedProductId: null,
  saveError: null,
  saveSuccess: null,
  masterProductRelationships: []
}

export const getMasterProducts = createAsyncThunk<GygListDataResponse<MasterProduct>, GygQueryOptions, Redux>(
  'gygMasterProducts/getMasterProducts',
  async (params, { extra }) => {
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })
    const response = await extra.httpClient.get(`master-products/only-master-products?${urlParams}`)

    return response.data
  }
)

export const getMasterProductList = createAsyncThunk<
  GygListDataResponse<MenuContainersWithMasterProduct>,
  number,
  Redux
>('gygMasterProducts/getMasterProductList', async (_, { extra }) => {
  const response = await extra.httpClient.get(`menu-containers/pricing-list`)

  return response.data
})

export const updateMasterProductPrice = createAsyncThunk<
  GygListDataResponse<MenuContainersWithMasterProduct>,
  { id: number; payload: MasterProductTierPrice[] },
  Redux
>('gygMasterProducts/updateMasterProductPrice', async ({ id, payload }, { extra }) => {
  const response = await extra.httpClient.put(`master-products/${id}/prices/bulk`, payload)

  return response.data
})

export const createMasterProduct = createAsyncThunk<
  GygListDataResponse<MasterProduct>,
  { payload: MasterProductCreatePayload },
  Redux
>('gygMasterProducts/createMasterProduct', async ({ payload }, { extra }) => {
  const response = await extra.httpClient.post(`master-products/from-product`, payload)

  return response.data
})

export const updateMasterProductSections = createAsyncThunk<
  GygListDataResponse<MenuContainersWithMasterProduct>,
  { id: number; payload: MasterProductRelationshipSection },
  Redux
>('gygMasterProducts/updateMasterProductRelationshipSection', async ({ id, payload }, { extra }) => {
  const response = await extra.httpClient.patch(`master-products/${id}/sections`, payload)

  return response.data
})

const gygMasterProductListSlice = createSlice({
  name: 'gygMasterProducts',
  initialState,
  reducers: {
    resetSuccessAndError: state => {
      state.success = null
      state.error = null
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMasterProductList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMasterProductList.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainers = action.payload.data
      })
      .addCase(getMasterProductList.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getMasterProducts.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMasterProducts.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.masterProducts = action.payload.data
      })
      .addCase(getMasterProducts.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(createMasterProduct.pending, state => {
        state.loading = true
        state.saveError = null
        state.saveSuccess = null
      })
      .addCase(createMasterProduct.fulfilled, state => {
        state.loading = false
        state.saveSuccess = null
      })
      .addCase(createMasterProduct.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.saveError = action.error.message ?? 'Failed to create master product'
        state.saveSuccess = false
      })
      .addCase(updateMasterProductSections.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMasterProductSections.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.masterProductRelationships = action.payload.data
      })
      .addCase(updateMasterProductSections.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(updateMasterProductPrice.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(updateMasterProductPrice.fulfilled, state => {
        state.loading = false
        state.success = null
      })
      .addCase(updateMasterProductPrice.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.success = null
        state.error = action.error.message ?? 'Failed to update prices'
      })
  }
})

export const { resetSuccessAndError } = gygMasterProductListSlice.actions

export default gygMasterProductListSlice.reducer
